import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Paperclip, Send, Lock, FileText, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fileUrl, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Pill, Thumb } from "@/components/Bits";

function Attachment({ a }) {
  const isImage = (a.content_type || "").startsWith("image/");
  return (
    <a
      href={fileUrl(a.file_id)}
      target="_blank"
      rel="noreferrer"
      data-testid={`message-attachment-${a.file_id}`}
      className="mt-2 flex items-center gap-3 rounded-sm border border-line bg-white px-3 py-2 text-xs text-stone transition-colors hover:border-fog"
    >
      {isImage ? (
        <Thumb src={fileUrl(a.file_id)} alt={a.filename} className="h-10 w-12" />
      ) : (
        <span className="flex h-10 w-12 items-center justify-center rounded-sm border border-line bg-[#F1F0EC]">
          <FileText className="h-4 w-4 text-fog" strokeWidth={1.5} />
        </span>
      )}
      <span className="truncate">{a.filename}</span>
    </a>
  );
}

function Bubble({ m, mine }) {
  const initials = (m.author_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");
  return (
    <div data-testid={`message-${m.id}`} className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`}>
      <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ink text-[10px] font-medium text-white">
        {initials}
      </span>
      <div className={`max-w-[78%] ${mine ? "text-right" : ""}`}>
        {m.context_label && (
          <Link
            to={m.related_selection_id ? `/products/${m.related_selection_id}` : `/presentations/${m.related_presentation_id}`}
            data-testid={`message-context-${m.id}`}
            className="mb-1 inline-block rounded-sm border border-line bg-[#F4F5E4] px-2 py-1 text-[11px] text-stone hover:border-lime"
          >
            Regarding: {m.context_label}
          </Link>
        )}
        <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px] text-fog">
          <span className={mine ? "order-2" : ""}>{m.author_name}</span>
          <span className="num">{timeAgo(m.created_at)}</span>
          {m.internal && (
            <Pill tone="bg-[#F6DED6] text-[#A8492E]" testId={`message-internal-${m.id}`}>
              Internal only
            </Pill>
          )}
        </div>
        <div
          className={`inline-block whitespace-pre-wrap rounded-sm px-4 py-3 text-sm leading-relaxed ${
            m.internal
              ? "border border-dashed border-[#E0B4A5] bg-[#FBF1ED] text-ink"
              : mine
              ? "bg-ink text-white"
              : "border border-line bg-white text-ink"
          }`}
        >
          {m.body}
        </div>
        <div className={mine ? "flex flex-col items-end" : ""}>
          {(m.attachments || []).map((a) => (
            <Attachment key={a.file_id} a={a} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Composer({ projectId, onSent }) {
  const { isStaff } = useAuth();
  const [text, setText] = useState("");
  const [internal, setInternal] = useState(false);
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const attach = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/projects/${projectId}/messages/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPending((p) => [...p, data]);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const send = async () => {
    if (!text.trim() && !pending.length) return;
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/messages`, {
        body: text.trim(),
        attachments: pending.map((p) => ({ file_id: p.file_id })),
        internal,
      });
      setText("");
      setPending([]);
      onSent();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-surface p-4" data-testid="message-composer">
      <textarea
        data-testid="message-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder={internal ? "Internal note — the client will not see this…" : "Write a message…"}
        className="w-full resize-none rounded-sm border border-line bg-white px-3 py-2 text-sm outline-none transition-colors placeholder:text-fog focus:border-lime"
      />
      {pending.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="pending-attachments">
          {pending.map((p) => (
            <span key={p.file_id} className="flex items-center gap-2 rounded-sm border border-line bg-white px-3 py-1.5 text-xs text-stone">
              <FileText className="h-3.5 w-3.5 text-fog" strokeWidth={1.5} />
              <span className="max-w-[160px] truncate">{p.filename}</span>
              <button
                data-testid={`remove-attachment-${p.file_id}`}
                onClick={() => setPending((prev) => prev.filter((x) => x.file_id !== p.file_id))}
                aria-label="Remove attachment"
              >
                <X className="h-3.5 w-3.5 text-fog hover:text-clay" strokeWidth={1.5} />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-sm border border-line px-3 py-2 text-xs text-stone transition-colors hover:border-fog">
            <Paperclip className="h-3.5 w-3.5" strokeWidth={1.5} />
            Attach
            <input
              ref={fileRef}
              data-testid="message-attach-input"
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={attach}
              disabled={busy}
            />
          </label>
          {isStaff && (
            <button
              data-testid="message-internal-toggle"
              onClick={() => setInternal((v) => !v)}
              className={`inline-flex items-center gap-2 rounded-sm border px-3 py-2 text-xs transition-colors ${
                internal ? "border-[#E0B4A5] bg-[#FBF1ED] text-[#A8492E]" : "border-line text-stone hover:border-fog"
              }`}
            >
              <Lock className="h-3.5 w-3.5" strokeWidth={1.5} />
              {internal ? "Internal note" : "Visible to client"}
            </button>
          )}
        </div>
        <button
          data-testid="message-send-button"
          onClick={send}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-lime-dark disabled:opacity-50"
        >
          <Send className="h-4 w-4" strokeWidth={1.5} />
          {busy ? "Sending…" : "Send"}
        </button>
      </div>
    </div>
  );
}

export default function Messages() {
  const { user, isStaff } = useAuth();
  const [threads, setThreads] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const endRef = useRef();

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/messages/threads");
      setThreads(data);
      setLoadError(false);
      setActive((cur) => cur || data[0]?.project_id || null);
    } catch {
      setThreads([]);
      setLoadError(true);
    }
  };

  const loadThread = async (pid) => {
    if (!pid) return;
    try {
      const { data } = await api.get(`/projects/${pid}/messages`);
      setThread(data);
      await api.post(`/projects/${pid}/messages/read`, {});
      loadThreads();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  useEffect(() => {
    loadThreads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadThread(active);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [thread]);

  return (
    <div className="space-y-8" data-testid="messages-page">
      <PageHeader
        overline={isStaff ? "Client Conversations" : "Talk To Your Designer"}
        title="Messages"
        subtitle={
          isStaff
            ? "One running thread per project. Internal notes stay with your team."
            : "Ask anything about your project — your design team replies right here."
        }
        testId="messages-header"
      />

      {threads === null ? (
        <div className="py-20 text-center text-sm text-fog" data-testid="messages-loading">Loading conversations…</div>
      ) : loadError ? (
        <div className="card-surface flex flex-col items-start gap-4 p-8" data-testid="messages-error">
          <h3 className="font-serif text-2xl text-ink">We couldn’t load your conversations</h3>
          <p className="text-sm text-stone">Your session may have expired. Try again, or sign in once more.</p>
          <button
            data-testid="messages-retry-btn"
            onClick={() => { setThreads(null); setLoadError(false); loadThreads(); }}
            className="rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark"
          >
            Try again
          </button>
        </div>
      ) : threads.length === 0 ? (
        <EmptyState
          testId="messages-empty"
          title="No conversations yet"
          body="Once a project is underway, your conversation will live here."
        />
      ) : (
        <div className={`grid gap-8 ${threads.length > 1 ? "lg:grid-cols-[300px_1fr]" : ""}`}>          {threads.length > 1 && (
            <div className="card-surface divide-y divide-line" data-testid="thread-list">
              {threads.map((t) => (
                <button
                  key={t.project_id}
                  data-testid={`thread-${t.project_id}`}
                  onClick={() => setActive(t.project_id)}
                  className={`flex w-full items-start gap-3 px-4 py-4 text-left transition-colors ${
                    active === t.project_id ? "bg-[#EDEFC4]" : "hover:bg-bone"
                  }`}
                >
                  <Thumb src={t.cover_image} alt={t.project_name} className="h-11 w-14" />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className={`truncate text-sm ${t.unread > 0 ? "font-medium text-ink" : "text-ink"}`}>
                        {t.client_name || t.project_name}
                      </span>
                      {t.unread > 0 && (
                        <span data-testid={`thread-unread-${t.project_id}`} className="num rounded-full bg-lime px-2 py-[1px] text-[10px] text-ink">
                          {t.unread}
                        </span>
                      )}
                    </span>
                    {t.client_name && <span className="block truncate text-[11px] text-fog">{t.project_name}</span>}
                    <span className="mt-1 flex items-center justify-between gap-2">
                      <span className="truncate text-xs text-stone">{t.last_message || "No messages yet"}</span>
                      <span className="num shrink-0 text-[10px] text-fog">{t.last_at ? timeAgo(t.last_at) : ""}</span>
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className="space-y-5">
            <div className="card-surface max-h-[58vh] space-y-6 overflow-auto p-6" data-testid="message-thread">
              {(thread?.messages || []).map((m) => (
                <Bubble key={m.id} m={m} mine={m.author_id === user?.id} />
              ))}
              {thread && thread.messages.length === 0 && (
                <div className="py-10 text-center text-sm text-fog" data-testid="thread-empty">
                  No messages in this conversation yet — say hello.
                </div>
              )}
              <div ref={endRef} />
            </div>
            {active && <Composer projectId={active} onSent={() => loadThread(active)} />}
          </div>
        </div>
      )}
    </div>
  );
}
